import { useForm } from "@mantine/form";
import { PasswordInput, Text, Paper, PaperProps, Button, Anchor, Stack, Alert } from "@mantine/core";
import { useMutation } from "react-query";
import { resetPassword } from "./Authentication";
import { useSearchParams } from "react-router-dom";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";

export function ResetPasswordForm(props: PaperProps) {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") ?? "";

    const form = useForm({
        initialValues: {
            password: "",
            confirmPassword: "",
        },
        validate: {
            password: (val) => (val.length < 6 ? "Password should include at least 6 characters" : null),
            confirmPassword: (val, values) => (val !== values.password ? "Passwords do not match" : null),
        },
    });

    const mutation = useMutation({
        mutationFn: (values: { password: string }) => {
            return resetPassword(token, values.password);
        },
    });

    const { isLoading, isSuccess, error } = mutation;

    let errorMessage = "";
    if (error) {
        errorMessage = (error as any)?.response?.data?.message || "Something went wrong, please try again";
    }

    if (!token) {
        return (
            <Paper radius="md" withBorder className="w-[30rem] p-12 max-w-[100vw] mt-[25%]" {...props}>
                <Stack align="center" gap="lg">
                    <IconAlertCircle size={48} color="#fa5252" />
                    <h1 className="text-center">Invalid link</h1>
                    <Text ta="center" c="dimmed">
                        This password reset link is invalid or has expired.
                    </Text>
                    <Anchor href="/forgot-password" size="sm">
                        Request a new link
                    </Anchor>
                </Stack>
            </Paper>
        );
    }

    if (isSuccess) {
        return (
            <Paper radius="md" withBorder className="w-[30rem] p-12 max-w-[100vw] mt-[25%]" {...props}>
                <Stack align="center" gap="lg">
                    <IconCheck size={48} color="#2f9e44" />
                    <h1 className="text-center">Password reset!</h1>
                    <Text ta="center" c="dimmed">
                        Your password has been successfully updated. You can now sign in with your new password.
                    </Text>
                    <Anchor href="/login" size="sm">
                        Go to sign in
                    </Anchor>
                </Stack>
            </Paper>
        );
    }

    return (
        <Paper radius="md" withBorder className="w-[30rem] p-12 max-w-[100vw] mt-[25%]" {...props}>
            <h1 className="mb-4">Reset password</h1>
            <Text c="dimmed" size="sm" mb="xl">
                Choose a new password for your account.
            </Text>

            {errorMessage && (
                <Alert icon={<IconAlertCircle size="1rem" />} title={errorMessage} color="red" mb="md" />
            )}

            <form
                onSubmit={form.onSubmit((values) => {
                    mutation.mutate(values);
                })}
            >
                <Stack>
                    <PasswordInput
                        required
                        withAsterisk={false}
                        label="New password"
                        value={form.values.password}
                        onChange={(event) => form.setFieldValue("password", event.currentTarget.value)}
                        error={form.errors.password}
                        radius="md"
                    />
                    <PasswordInput
                        required
                        withAsterisk={false}
                        label="Confirm new password"
                        value={form.values.confirmPassword}
                        onChange={(event) => form.setFieldValue("confirmPassword", event.currentTarget.value)}
                        error={form.errors.confirmPassword}
                        radius="md"
                    />
                    <Button type="submit" loading={isLoading} fullWidth mt="xl" size="lg">
                        Reset password
                    </Button>
                    <Text ta="center" mt="md">
                        <Anchor href="/login" size="sm">
                            Back to sign in
                        </Anchor>
                    </Text>
                </Stack>
            </form>
        </Paper>
    );
}
